/**
 * "Seen on home & search" (BR-21, D-55): a product card counts as seen when ≥ 50 % of it is on
 * screen in Home, Search or a category list — once per product per device per IST day. The ids
 * already counted today are kept on the device; new ones are sent as `seen` events (≤ 60 ids).
 */
import { safeStorage } from '@/lib/storage'
import { istDay } from '@/lib/time'

import { track } from './tracker'

const KEY = 'angadi.seen'
export const MAX_SEEN_IDS = 60
const MAX_KEPT = 3000
const SEND_DELAY_MS = 2000

interface Stored {
  day: string
  ids: number[]
}

function read(day: string): Set<number> {
  try {
    const s = JSON.parse(safeStorage.get(KEY) ?? 'null') as Stored | null
    return new Set(s && s.day === day && Array.isArray(s.ids) ? s.ids : [])
  } catch {
    return new Set()
  }
}

let pending: number[] = []
let timer: ReturnType<typeof setTimeout> | undefined

/** Send what's pending as `seen` events of ≤ 60 ids. */
export function flushSeen(): void {
  if (timer) clearTimeout(timer)
  timer = undefined
  while (pending.length) track('seen', { product_ids: pending.splice(0, MAX_SEEN_IDS) })
}

/** Record that a product card was seen. Returns true when it's the first time today. */
export function markSeen(productId: number, now: number = Date.now()): boolean {
  try {
    const day = istDay(now)
    const ids = read(day)
    if (ids.has(productId)) return false
    ids.add(productId)
    safeStorage.set(KEY, JSON.stringify({ day, ids: [...ids].slice(-MAX_KEPT) }))
    pending.push(productId)
    if (pending.length >= MAX_SEEN_IDS) flushSeen()
    else timer ??= setTimeout(flushSeen, SEND_DELAY_MS)
    return true
  } catch {
    return false
  }
}

// ---------------------------------------------------------------- one shared observer for cards

const watched = new WeakMap<Element, number>()
let observer: IntersectionObserver | null = null

function getObserver(): IntersectionObserver | null {
  if (observer) return observer
  if (typeof IntersectionObserver === 'undefined') return null
  observer = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting || e.intersectionRatio < 0.5) continue
        const id = watched.get(e.target)
        if (id != null) markSeen(id)
        observer?.unobserve(e.target)
      }
    },
    { threshold: 0.5 },
  )
  return observer
}

/** Watch a card element; returns the cleanup. */
export function observeSeen(el: Element, productId: number): () => void {
  const o = getObserver()
  if (!o) return () => undefined
  watched.set(el, productId)
  o.observe(el)
  return () => {
    o.unobserve(el)
    watched.delete(el)
  }
}

/** Test hook: forget the observer and pending ids. */
export function resetSeenForTests(): void {
  observer?.disconnect()
  observer = null
  pending = []
  if (timer) clearTimeout(timer)
  timer = undefined
}
